import { Actions, createEffect, ofType } from '@ngrx/effects'
import { FolderOverviewApiMappingService, FolderOverviewApiService } from '../services'
import { FolderScopesSelectors } from '../selectors'
import { SNACKBAR_LENGTH_SHORT, SnackbarEventType, SnackbarService } from '@app/shared/utils/snackbar.service'
import { Store, select } from '@ngrx/store'
import { catchError, map, of, switchMap, withLatestFrom } from 'rxjs'

import { FolderScopesActions } from '../actions/folder-scopes.actions'
import { Injectable } from '@angular/core'
import { TableColumnPreferences } from '@app/shared/components/ui-components/scope-ui-table/table-column-preferences.type'
import { UserPreferences } from '@app/core/model/user-preferences.interface'
import { UserPreferencesColumns } from '@app/core/model/enums/user-preferences-columns.enum'
import { UserService } from '@app/features/user-account/services/user.service'
import { folderScopesColumnsBaseConfig } from '../const/folder-details-table'
import { mergePreferencesWithTableConfig } from '@app/shared/utils/utils'
import { ScopeElementsService } from '@app/shared/components/scope-elements/services/scope-elements.service'

@Injectable()
export class FolderScopesEffects {
  constructor(
    private store$: Store,
    private actions$: Actions,
    private apiService: FolderOverviewApiService,
    private userService: UserService,
    private snackbarService: SnackbarService,
    private apiMappingService: FolderOverviewApiMappingService,
    private scopeElementsService: ScopeElementsService
  ) {}

  getColumnPreference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderScopesActions.getColumnPreference),
      switchMap(() =>
        this.userService.getUserPreferences(UserPreferencesColumns.FOLDER_OVERVIEW_USERCOLUMNS).pipe(
          map((preferences) => mergePreferencesWithTableConfig(folderScopesColumnsBaseConfig, preferences)),
          map((columns) => FolderScopesActions.getColumnPreferenceSuccess({ columns })),
          catchError((error) => of(FolderScopesActions.getColumnPreferenceFail({ error })))
        )
      )
    )
  )

  updateColumnPreference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderScopesActions.updateColumnPreference),
      withLatestFrom(this.store$.pipe(select(FolderScopesSelectors.selectColumns))),
      switchMap(([{ preference }, currentPreferences]) => {
        const updatedPreferences: TableColumnPreferences = { ...currentPreferences, [preference.key]: preference }

        const userPreferences: UserPreferences = updatedPreferences as UserPreferences

        return this.userService
          .setUserPreferences(UserPreferencesColumns.FOLDER_OVERVIEW_USERCOLUMNS, userPreferences)
          .pipe(map(() => FolderScopesActions.updateColumnPreferenceSuccess({ columns: updatedPreferences })))
      }),
      catchError((error) => of(FolderScopesActions.updateColumnPreferenceFail({ error })))
    )
  )

  deleteScope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderScopesActions.deleteScope),
      switchMap(({ id }) =>
        this.apiService.deleteScope(id).pipe(
          map(() => {
            this.snackbarService.showSnackbar(
              'Scope was successfully deleted',
              SNACKBAR_LENGTH_SHORT,
              SnackbarEventType.SUCCESS
            )
            return FolderScopesActions.deleteScopeSuccess({ id })
          }),
          catchError((error) => of(FolderScopesActions.deleteScopeFail({ error })))
        )
      )
    )
  )

  removeScope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderScopesActions.removeScope),
      switchMap(({ id }) =>
        this.apiService.removeScope(id).pipe(
          map(() => {
            this.snackbarService.showSnackbar(
              'Scope was successfully removed from Folder',
              SNACKBAR_LENGTH_SHORT,
              SnackbarEventType.SUCCESS
            )
            return FolderScopesActions.removeScopeSuccess({ id })
          }),
          catchError((error) => of(FolderScopesActions.removeScopeFail({ error })))
        )
      )
    )
  )

  updateScope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderScopesActions.updateScope),
      switchMap(({ scope }) =>
        this.apiService.updateScope(scope.identity.id, scope).pipe(
          map(() => FolderScopesActions.updateScopeSuccess({ scope })),
          catchError((error) => of(FolderScopesActions.updateScopeFail({ error })))
        )
      )
    )
  )

  updateDeliverable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderScopesActions.updateDeliverable),
      switchMap(({ scope, deliverable }) =>
        this.apiService.updateDeliverable(scope.identity.id, deliverable).pipe(
          map(() => FolderScopesActions.updateDeliverableSuccess({ scope, deliverable })),
          catchError((error) => of(FolderScopesActions.updateDeliverableFail({ error })))
        )
      )
    )
  )

  updateComponent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderScopesActions.updateComponent),
      switchMap(({ scope, component }) =>
        this.apiService.updateComponent(scope.identity.id, component).pipe(
          map(() => FolderScopesActions.updateComponentSuccess({ scope, component })),
          catchError((error) => of(FolderScopesActions.updateComponentFail({ error })))
        )
      )
    )
  )

  getScopeDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderScopesActions.getScopeDetails),
      switchMap(({ scope }) =>
        this.apiService.getScopeById(scope.identity.id, scope.version).pipe(
          map((scope) => {
            this.scopeElementsService.setUserColumns(scope)
            return FolderScopesActions.getScopeDetailsSuccess({ scope })
          }),
          catchError((error) => of(FolderScopesActions.getScopeDetailsFail({ error, id: scope.identity.id })))
        )
      )
    )
  )

  getDeliverableDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FolderScopesActions.getDeliverableDetails),
      switchMap(({ scopeId, deliverableId }) =>
        this.apiService.getDeliverableById(scopeId, deliverableId).pipe(
          map((deliverable) => FolderScopesActions.getDeliverableDetailsSuccess({ scopeId, deliverable })),
          catchError((error) => of(FolderScopesActions.getDeliverableDetailsFail({ error })))
        )
      )
    )
  )
}
